import { type ComponentProps, type ComponentType, type ReactNode } from 'react';
import { type IconName } from '@frontend/icons';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';

export interface DemoPanelHeaderConfig {
  icon?: ReactNode;
  iconName?: IconName;
  title?: ReactNode;
  description?: ReactNode;
  action?: { render: ReactNode } | { title: string; trackingId: string; onClick: () => void };
}

export type DemoPanelEntry<TComponent extends ComponentType<any> = ComponentType<any>> = {
  /** Unique key, e.g. 'demo-contact-panel', 'call-insight'. */
  id: string;
  /** The component to render inside the drawer. Use `useDemoPanel()` for scope props. */
  component: TComponent;
  /** Props to pass to the component. Type is inferred from the component. */
  componentProps?: ComponentProps<TComponent>;
  /** Preferred width in px. Defaults to 600 if omitted. */
  width?: number;
};

const DEFAULT_MAX_PANELS = 3;

interface DemoPanelState {
  /** Ordered stack of panels. Last element = top of stack (highest priority). */
  panels: DemoPanelEntry<ComponentType<any>>[];
  /** Maximum number of panels allowed in the stack. */
  maxPanels: number;
  /** Header configuration for the drawer banner, shared across all panels. */
  headerConfig: DemoPanelHeaderConfig | undefined;

  /**
   * Register a panel in the stack.
   * - If the `id` already exists, moves it to the top and updates its config.
   * - If the stack is at `maxPanels`, the oldest (first) panel is evicted.
   */
  register: <TComp extends ComponentType<any>>(entry: DemoPanelEntry<TComp>) => void;
  /** Remove a panel by id. When the stack becomes empty the drawer auto-closes. */
  unregister: (id: string) => void;
  /** Clear the entire stack, closing the drawer. */
  closeAll: () => void;
  /** Set the header configuration for the drawer banner. */
  setHeaderConfig: (config: DemoPanelHeaderConfig | undefined) => void;
  /** Reset the entire store to its initial state. */
  reset: () => void;
}

export const useDemoPanelStore = createStoreWithSubscribe<DemoPanelState>(
  (set, get) => ({
    panels: [],
    maxPanels: DEFAULT_MAX_PANELS,
    headerConfig: undefined,

    register: (entry) => {
      set(
        (state) => {
          const existing = state.panels.findIndex((p) => p.id === entry.id);
          let next = [...state.panels];

          if (existing !== -1) {
            next.splice(existing, 1);
          }

          next.push(entry);

          if (next.length > get().maxPanels) {
            next = next.slice(next.length - get().maxPanels);
          }

          state.panels = next;
        },
        false,
        'DEMO_PANEL_REGISTER'
      );
    },

    unregister: (id) => {
      set(
        (state) => {
          state.panels = state.panels.filter((p) => p.id !== id);
        },
        false,
        'DEMO_PANEL_UNREGISTER'
      );
    },

    closeAll: () => {
      set(
        (state) => {
          state.panels = [];
          state.headerConfig = undefined;
        },
        false,
        'DEMO_PANEL_CLOSE_ALL'
      );
    },

    setHeaderConfig: (config) => {
      set(
        (state) => {
          state.headerConfig = config;
        },
        false,
        'DEMO_PANEL_SET_HEADER_CONFIG'
      );
    },

    reset: () => {
      set(
        (state) => {
          state.panels = [];
          state.headerConfig = undefined;
        },
        false,
        'DEMO_PANEL_RESET'
      );
    },
  }),
  { name: 'DemoPanelStore', trace: true }
);

export const useDemoPanelShallowStore = createShallowStore(useDemoPanelStore);
